import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, fmtPct } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { ArrowLeft, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDealCalculations } from "@/hooks/useDealCalculations";

const Row = ({ label, value, bold, accent }) => (
  <div className="flex justify-between items-baseline py-2 border-b border-border/50 last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`font-mono ${bold ? "font-bold text-base" : ""} ${accent || ""}`}>{value}</span>
  </div>
);

export default function DealSheet() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/vehicles/${id}`).then((r) => setData(r.data)); }, [id]);
  const calc = useDealCalculations(data);
  if (!data || !calc) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const { vehicle: v, costs, sale } = data;
  const { repair_total, total_investment, sale_price, profit, roi, daysHeld, accent } = calc;

  let heroBg = "";
  if (profit !== null) {
    heroBg = profit >= 0
      ? "bg-emerald-50/60 dark:bg-emerald-500/5 border-emerald-200/60"
      : "bg-red-50/60 dark:bg-red-500/5 border-red-200/60";
  }

  const print = () => window.print();

  return (
    <div data-testid="deal-sheet-page">
      <Link to={`/inventory/${id}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={14} /> Back to Vehicle
      </Link>
      <PageHeader
        overline={`Stock ${v.stock_number}`}
        title="Deal Sheet"
        description={`${v.year} ${v.make} ${v.model} ${v.trim || ""} · VIN ${v.vin || "—"}`}
        action={<Button data-testid="print-deal-sheet" onClick={print} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"><FileDown size={14} className="mr-2" />Print</Button>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-semibold">Cost Breakdown</h3>
            <StatusBadge status={v.status} />
          </div>
          <Row label="Purchase Price" value={fmtMoney(v.purchase_price)} />
          <Row label="Tax" value={fmtMoney(v.tax_cost)} />
          <Row label="Dealer Fee" value={fmtMoney(v.dealer_fee)} />
          <Row label="Auction / Transport Fees" value={fmtMoney(costs.auction_fees_total - (v.dealer_fee || 0))} />
          <Row label="Repairs" value={fmtMoney(repair_total)} />
          <div className="mt-3 pt-3 border-t-2 border-foreground">
            <Row label="Total Investment" value={fmtMoney(total_investment)} bold />
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-xl font-semibold mb-4">Sale Performance</h3>
          <Row label="Sale Price" value={sale ? fmtMoney(sale_price) : "—"} />
          <Row label="Net Profit" value={profit === null ? "—" : fmtMoney(profit)} accent={accent} bold />
          <Row label="ROI %" value={roi === null ? "—" : fmtPct(roi)} accent={accent} bold />
          <Row label="Days Held" value={`${daysHeld} days`} />
          <Row label="Source" value={(v.source || v.auction_house || "—").toUpperCase()} />
          <Row label="Purchase Date" value={fmtDate(v.purchase_date)} />
          {sale && <Row label="Sale Date" value={fmtDate(sale.sale_date || sale.created_at)} />}
          {sale && <Row label="Invoice" value={<Link to={`/sales/${sale.id}`} className="text-blue-600 hover:underline">{sale.invoice_number}</Link>} />}
        </Card>
      </div>

      <Card className={`mt-6 p-6 ${heroBg}`}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            ["Total Investment", fmtMoney(total_investment), ""],
            ["Sale Price", sale ? fmtMoney(sale_price) : "Not Sold", ""],
            ["Net Profit", profit === null ? "—" : fmtMoney(profit), accent],
            ["ROI %", roi === null ? "—" : fmtPct(roi), accent],
          ].map(([l, val, a]) => (
            <div key={l} className="text-center">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{l}</div>
              <div className={`font-display text-3xl md:text-4xl font-bold mt-2 ${a}`}>{val}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
